import _createForOfIteratorHelperLoose from "@babel/runtime/helpers/esm/createForOfIteratorHelperLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _dec, _class, _class2, _temp;

import React from 'react';
import invariant from 'invariant';
import { globalObject } from '@skbkontur/global-object';
import debounce from 'lodash.debounce';
import { isKeyArrowDown, isKeyArrowUp, isKeyArrowVertical, isKeyEnter, isKeyEscape, isKeySpace } from "../../../lib/events/keyboard/identifiers";
import { locale } from "../../../lib/locale/decorators";
import { reactGetTextContent } from "../../../lib/reactGetTextContent";
import { Button } from "../../Button";
import { DropdownContainer } from "../../../internal/DropdownContainer";
import { filterProps } from "../../../lib/filterProps";
import { Input } from "../../Input";
import { Menu } from "../../../internal/Menu";
import { MenuItem } from "../../MenuItem";
import { MenuSeparator } from "../../MenuSeparator";
import { RenderLayer } from "../../../internal/RenderLayer";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { getRandomID, isFunction, isNonNullable, isReactUINode } from "../../../lib/utils";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { ArrowChevronDownIcon } from "../../../internal/icons/16px";
import { MobilePopup } from "../../../internal/MobilePopup";
import { cx } from "../../../lib/theming/Emotion";
import { responsiveLayout } from "../../ResponsiveLayout/decorator";
import { getRootNode, rootNode } from "../../../lib/rootNode";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { ThemeFactory } from "../../../lib/theming/ThemeFactory";
import { getFullReactUIFlagsContext, ReactUIFeatureFlagsContext } from "../../../lib/featureFlagsContext";
import { ArrowDownIcon } from "../ArrowDownIcon";
import { Item } from "../Item";
import { SelectLocaleHelper } from "../locale";
import { styles } from "../Select.styles";
import { getSelectTheme } from "../selectTheme";
var PASS_BUTTON_PROPS = {
  disabled: true,
  error: true,
  use: true,
  size: true,
  warning: true,
  onMouseEnter: true,
  onMouseLeave: true,
  onMouseOver: true
};
export var SelectDataTids = {
  root: 'Select__root',
  menu: 'Select__menu',
  label: 'Select__label'
};
export var SelectIds = {
  menu: SelectDataTids.menu
};
export var Select = (_dec = locale('Select', SelectLocaleHelper), responsiveLayout(_class = rootNode(_class = _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Select, _React$Component);

  function Select() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      opened: false,
      value: _this.props.defaultValue,
      searchPattern: ''
    };
    _this.menuId = SelectIds.menu + getRandomID();
    _this.buttonElement = null;
    _this.getProps = createPropsGetter(Select.defaultProps);

    _this.open = function () {
      if (!_this.state.opened) {
        _this.setState({
          opened: true
        });

        if (_this.props.onOpen) {
          _this.props.onOpen();
        }
      }
    };

    _this.close = function () {
      if (_this.state.opened) {
        _this.setState({
          opened: false,
          searchPattern: ''
        });

        if (_this.props.onClose) {
          _this.props.onClose();
        }
      }
    };

    _this.focus = function () {
      if (_this.buttonElement && _this.buttonElement.focus) {
        _this.buttonElement.focus();
      }
    };

    _this.getDefaultButtonParams = function () {
      var _this$renderLabel = _this.renderLabel(),
          label = _this$renderLabel.label,
          isPlaceholder = _this$renderLabel.isPlaceholder;

      var buttonParams = {
        opened: _this.state.opened,
        label: label,
        isPlaceholder: isPlaceholder,
        onClick: _this.toggle,
        onKeyDown: _this.handleKey,
        size: _this.getProps().size,
        disabled: _this.getProps().disabled
      };
      return buttonParams;
    };

    _this.getSearch = function () {
      return /*#__PURE__*/React.createElement("div", {
        className: styles.search(),
        onKeyDown: _this.handleKey
      }, /*#__PURE__*/React.createElement(Input, {
        ref: _this.debouncedFocusInput,
        onValueChange: _this.handleSearch,
        width: "100%"
      }));
    };

    _this.getMobileSearch = function () {
      return /*#__PURE__*/React.createElement(Input, {
        autoFocus: true,
        value: _this.state.searchPattern,
        ref: _this.debouncedFocusInput,
        onValueChange: _this.handleSearch,
        width: "100%"
      });
    };

    _this.getMenuItems = function (value) {
      var isMobile = _this.isMobileLayout;
      var size = _this.props.size;
      return _this.mapItems(function (iValue, item, i, comment) {
        if (isFunction(item)) {
          var element = item();

          if ( /*#__PURE__*/React.isValidElement(element)) {
            return /*#__PURE__*/React.cloneElement(element, {
              key: i,
              isMobile: isMobile,
              size: size
            });
          }

          return null;
        }

        if ( /*#__PURE__*/React.isValidElement(item)) {
          if (isReactUINode('MenuItem', item)) {
            return /*#__PURE__*/React.cloneElement(item, {
              key: i,
              isMobile: isMobile,
              size: size
            });
          }

          if (isReactUINode('MenuHeader', item)) {
            return /*#__PURE__*/React.cloneElement(item, {
              size: size
            });
          }

          return /*#__PURE__*/React.cloneElement(item, {
            key: i
          });
        }

        return /*#__PURE__*/React.createElement(MenuItem, {
          key: i,
          state: _this.areValuesEqual(iValue, value) ? 'selected' : null,
          onClick: _this.select.bind(_assertThisInitialized(_this), iValue),
          comment: comment,
          isMobile: isMobile,
          size: _this.props.size
        }, _this.getProps().renderItem(iValue, item));
      });
    };

    _this.dropdownContainerGetParent = function () {
      return getRootNode(_assertThisInitialized(_this));
    };

    _this.focusInput = function (input) {
      return input == null ? void 0 : input.focus();
    };

    _this.debouncedFocusInput = debounce(_this.focusInput);

    _this.refMenu = function (menu) {
      _this.menu = menu;
    };

    _this.toggle = function () {
      if (_this.state.opened) {
        _this.close();
      } else {
        _this.open();
      }
    };

    _this.handleKey = function (e) {
      if (!_this.state.opened) {
        if (isKeySpace(e) || isKeyArrowVertical(e)) {
          e.preventDefault();

          _this.open();
        }
      } else {
        switch (true) {
          case isKeyEscape(e):
            _this.focus();

            _this.close();

            break;

          case isKeyArrowUp(e):
            e.preventDefault();

            if (_this.menu) {
              _this.menu.up();
            }

            break;

          case isKeyArrowDown(e):
            e.preventDefault();

            if (_this.menu) {
              _this.menu.down();
            }

            break;

          case isKeyEnter(e):
            e.preventDefault(); // To prevent form submission.

            if (_this.menu) {
              _this.menu.enter(e);
            }

            break;
        }
      }

      if (_this.props.onKeyDown) {
        _this.props.onKeyDown(e);
      }
    };

    _this.handleSearch = function (value) {
      var _this$menu;

      var menuItemsAtAnyLevel = _this.featureFlags.menuItemsAtAnyLevel;

      _this.setState({
        searchPattern: value
      });

      (_this$menu = _this.menu) == null ? void 0 : _this$menu.highlightItem(menuItemsAtAnyLevel ? 0 : 1);
    };

    _this.buttonRef = function (element) {
      _this.buttonElement = element;
    };

    _this.getButton = function (buttonParams) {
      var _buttonElement$props$;

      var button = _this.props._renderButton ? _this.props._renderButton(buttonParams) : _this.renderDefaultButton(buttonParams);
      var buttonElement = React.Children.only(button);
      return /*#__PURE__*/React.isValidElement(buttonElement) ? /*#__PURE__*/React.cloneElement(buttonElement, {
        ref: _this.buttonRef,
        onFocus: _this.props.onFocus,
        onBlur: _this.props.onBlur,
        size: _this.props.size,
        'aria-describedby': _this.props['aria-describedby'],
        'aria-expanded': _this.state.opened ? 'true' : 'false',
        'aria-controls': _this.menuId,
        'aria-label': (_buttonElement$props$ = buttonElement.props['aria-label']) != null ? _buttonElement$props$ : _this.props['aria-label']
      }) : buttonElement;
    };

    return _this;
  }

  var _proto = Select.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(_prevProps, prevState) {
    if (!prevState.opened && this.state.opened) {
      globalObject.addEventListener == null ? void 0 : globalObject.addEventListener('popstate', this.close);
    }

    if (prevState.opened && !this.state.opened) {
      globalObject.removeEventListener == null ? void 0 : globalObject.removeEventListener('popstate', this.close);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ReactUIFeatureFlagsContext.Consumer, null, function (flags) {
      _this2.featureFlags = getFullReactUIFlagsContext(flags);
      return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
        _this2.theme = ThemeFactory.create({
          menuOffsetY: theme.selectMenuOffsetY
        }, theme);
        return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
          value: _this2.theme
        }, _this2.renderMain());
      });
    });
  }
  /**
  * @public
  */
  ;

  _proto.getMenuRenderer = function getMenuRenderer() {
    if (this.props.disabled) {
      return null;
    }

    if (this.isMobileLayout) {
      return this.renderMobileMenu();
    }

    if (this.state.opened) {
      return this.renderMenu();
    }

    return null;
  };

  _proto.renderMain = function renderMain() {
    var _cx;

    var buttonParams = this.getDefaultButtonParams();
    var button = /*#__PURE__*/React.createElement(ThemeContext.Provider, {
      value: getSelectTheme(this.theme, this.props)
    }, this.getButton(buttonParams));
    var isMobile = this.isMobileLayout;
    var style = {
      width: this.props.width,
      maxWidth: this.props.maxWidth || undefined
    };
    var root = /*#__PURE__*/React.createElement("span", {
      "data-tid": SelectDataTids.root,
      className: cx((_cx = {}, _cx[styles.root()] = true, _cx[styles.rootMobile(this.theme)] = isMobile, _cx)),
      style: style
    }, button, this.getMenuRenderer());
    return /*#__PURE__*/React.createElement(CommonWrapper, _extends({
      rootNodeRef: this.setRootNode
    }, this.props), /*#__PURE__*/React.createElement(RenderLayer, {
      onClickOutside: this.close,
      onFocusOutside: this.close,
      active: isMobile ? false : this.state.opened
    }, root));
  };

  _proto.renderLabel = function renderLabel() {
    var _this$locale;

    var value = this.getValue();
    var item = this.getItemByValue(value);

    if (isNonNullable(value)) {
      return {
        label: this.getProps().renderValue(value, item),
        isPlaceholder: false
      };
    }

    return {
      label: /*#__PURE__*/React.createElement("span", null, this.props.placeholder || ((_this$locale = this.locale) == null ? void 0 : _this$locale.placeholder)),
      isPlaceholder: true
    };
  };

  _proto.getLeftIconClass = function getLeftIconClass(size) {
    if (this.getProps().use === 'link') {
      return styles.leftIconLink(this.theme);
    }

    switch (size) {
      case 'large':
        return styles.leftIconLarge(this.theme);

      case 'medium':
        return styles.leftIconMedium(this.theme);

      case 'small':
      default:
        return styles.leftIconSmall(this.theme);
    }
  };

  _proto.renderDefaultButton = function renderDefaultButton(params) {
    var _cx2, _cx3;

    var buttonProps = _extends({}, filterProps(this.props, PASS_BUTTON_PROPS), {
      align: 'left',
      disabled: this.props.disabled,
      width: '100%',
      onClick: params.onClick,
      onKeyDown: params.onKeyDown,
      active: params.opened,
      size: params.size
    });

    var use = this.getProps().use;
    var labelProps = {
      'data-tid': SelectDataTids.label,
      className: cx((_cx2 = {}, _cx2[styles.label()] = use !== 'link', _cx2[styles.placeholder(this.theme)] = params.isPlaceholder, _cx2[styles.customUsePlaceholder()] = params.isPlaceholder && use !== 'default', _cx2[styles.placeholderDisabled(this.theme)] = params.isPlaceholder && this.props.disabled, _cx2)),
      style: {
        paddingRight: this.getSelectIconGap()
      }
    };
    var useIsCustom = use !== 'default';
    var icon = isTheme2022(this.theme) ? /*#__PURE__*/React.createElement(ArrowDownIcon, {
      size: this.props.size
    }) : /*#__PURE__*/React.createElement(ArrowChevronDownIcon, null);
    return /*#__PURE__*/React.createElement(Button, buttonProps, /*#__PURE__*/React.createElement("div", {
      className: styles.selectButtonContainer()
    }, this.props._icon && /*#__PURE__*/React.createElement("div", {
      className: this.getLeftIconClass(this.props.size)
    }, this.props._icon), /*#__PURE__*/React.createElement("span", labelProps, params.label), /*#__PURE__*/React.createElement("div", {
      className: cx(styles.arrowWrap(this.theme), (_cx3 = {}, _cx3[styles.arrowDisabled(this.theme)] = this.props.disabled, _cx3[styles.customUseArrow()] = useIsCustom, _cx3))
    }, icon)));
  };

  _proto.getSelectIconGap = function getSelectIconGap() {
    var _this3 = this;

    var getArrowPadding = function getArrowPadding() {
      switch (_this3.props.size) {
        case 'large':
          return _this3.theme.selectIconGapLarge;

        case 'medium':
          return _this3.theme.selectIconGapMedium;

        case 'small':
        default:
          return _this3.theme.selectIconGapSmall;
      }
    };

    var arrowLeftPadding = parseFloat(getArrowPadding()) || 0;
    return arrowLeftPadding;
  };

  _proto.renderMenu = function renderMenu() {
    var search = this.props.search ? this.getSearch() : null;
    var value = this.getValue();
    var hasFixedWidth = !!this.props.menuWidth && this.props.menuWidth !== 'auto';
    return /*#__PURE__*/React.createElement(DropdownContainer, {
      id: this.menuId,
      "data-tid": SelectDataTids.menu,
      getParent: this.dropdownContainerGetParent,
      align: this.props.menuAlign,
      disablePortal: this.props.disablePortal,
      hasFixedWidth: hasFixedWidth,
      menuPos: this.props.menuPos
    }, /*#__PURE__*/React.createElement(Menu, {
      ref: this.refMenu,
      width: this.props.menuWidth,
      onItemClick: this.close,
      maxHeight: this.props.maxMenuHeight,
      align: this.props.menuAlign
    }, search, this.getMenuItems(value)));
  };

  _proto.renderMobileMenu = function renderMobileMenu() {
    var search = this.props.search ? this.getMobileSearch() : null;
    var value = this.getValue();
    return /*#__PURE__*/React.createElement(MobilePopup, {
      headerChildComponent: search,
      caption: this.props.mobileMenuHeaderText,
      onCloseRequest: this.close,
      opened: this.state.opened
    }, /*#__PURE__*/React.createElement(Menu, {
      hasShadow: false,
      onItemClick: this.close,
      disableScrollContainer: true,
      maxHeight: 'auto'
    }, this.getMenuItems(value)));
  };

  _proto.select = function select(value) {
    this.focus();
    this.setState({
      opened: false,
      value: value
    });

    if (!this.areValuesEqual(this.getValue(), value)) {
      var _this$props$onValueCh, _this$props;

      (_this$props$onValueCh = (_this$props = this.props).onValueChange) == null ? void 0 : _this$props$onValueCh.call(_this$props, value);
    }
  };

  _proto.getValue = function getValue() {
    if (this.props.value !== undefined) {
      return this.props.value;
    }

    return this.state.value;
  };

  _proto.mapItems = function mapItems(fn) {
    var items = this.props.items;

    if (!items) {
      return [];
    }

    var pattern = this.state.searchPattern && this.state.searchPattern.toLowerCase();
    var result = [];
    var index = 0;

    for (var _iterator = _createForOfIteratorHelperLoose(items), _step; !(_step = _iterator()).done;) {
      var entry = _step.value;

      var _normalizeEntry = normalizeEntry(entry),
          _value = _normalizeEntry[0],
          _item = _normalizeEntry[1],
          _comment = _normalizeEntry[2];

      if (!pattern || this.getProps().filterItem(_value, _item, pattern)) {
        result.push(fn(_value, _item, index, _comment));
        ++index;
      }
    }

    return result;
  };

  _proto.getItemByValue = function getItemByValue(value) {
    if (value === null || value === undefined) {
      return null;
    }

    var items = this.props.items || [];

    for (var _iterator2 = _createForOfIteratorHelperLoose(items), _step2; !(_step2 = _iterator2()).done;) {
      var entry = _step2.value;

      var _normalizeEntry2 = normalizeEntry(entry),
          itemValue = _normalizeEntry2[0],
          _item2 = _normalizeEntry2[1];

      if (this.areValuesEqual(itemValue, value)) {
        return _item2;
      }
    }

    return null;
  };

  _proto.areValuesEqual = function areValuesEqual(value1, value2) {
    return isNonNullable(value1) && isNonNullable(value2) && this.getProps().areValuesEqual(value1, value2);
  };

  return Select;
}(React.Component), _class2.__KONTUR_REACT_UI__ = 'Select', _class2.displayName = 'Select', _class2.defaultProps = {
  renderValue: renderValue,
  renderItem: renderItem,
  areValuesEqual: areValuesEqual,
  filterItem: filterItem,
  use: 'default',
  size: 'small'
}, _class2.Item = Item, _class2.SEP = function () {
  return /*#__PURE__*/React.createElement(MenuSeparator, null);
}, _class2["static"] = function (element) {
  invariant( /*#__PURE__*/React.isValidElement(element) || typeof element === 'function', 'Select.static(element) expects element to be a valid react element.');
  return element;
}, _temp)) || _class) || _class) || _class);

function renderValue(value, item) {
  return item;
}

function renderItem(value, item) {
  return item;
}

function areValuesEqual(value1, value2) {
  return value1 === value2;
}

function normalizeEntry(entry) {
  if (Array.isArray(entry)) {
    return entry;
  }

  return [entry, entry, undefined];
}

var getTextFromItem = function getTextFromItem(item) {
  if (typeof item === 'string') {
    return item;
  }

  if (isFunction(item)) {
    return getTextFromItem(item());
  }

  if ( /*#__PURE__*/React.isValidElement(item)) {
    return reactGetTextContent(item);
  }

  if (typeof item === 'number') {
    return item.toString(10);
  }

  return '';
};

function filterItem(value, item, pattern) {
  if (item === Select.SEP) {
    return false;
  }

  var itemText = getTextFromItem(item);

  if (!itemText) {
    return false;
  }

  return itemText.toLowerCase().indexOf(pattern) !== -1;
}